import {
  AddressLookupResponseSchema,
  AddressSuggestResponseSchema,
  ServiceableZipsResponseSchema,
  BFF_ROUTES,
  LeadCaptureResponseSchema,
  SessionResponseSchema,
  type AddressLookupRequest,
  type AddressLookupResponse,
  type AddressSuggestResponse,
  type ServiceableZipsResponse,
  type LeadCaptureRequest,
  type LeadCaptureResponse,
  type SessionResponse,
} from '@rheora/enrollment-contract';

import type { DeepLinkSource } from '../schemas/shop.schema';

import { bffGet, bffPost } from './bff-client';

/**
 * Typed fetchers for E-SF03 serviceability. Thin: build the path, validate
 * against the shared contract schema, return. No caching, no state — that is the
 * queries layer's job. Pages never import these directly.
 *
 * ── PUBLIC POSTURE ─────────────────────────────────────────────────────────
 * All calls are credential-free (`bff-client`): no Authorization, no
 * x-tenant-id. The browser holds an opaque `candidateId`, never a raw ESIID
 * (AC3) — the BFF keeps the ESIID server-side, keyed to the funnel session.
 */

/**
 * Ensure a funnel session exists (mints the cookie if absent). The meter
 * sub-flows call this before the caller can submit; a session is required
 * downstream (AC3 — results are cached per session).
 *
 * ── IT ALSO REPORTS THE DEEP-LINK SOURCE ───────────────────────────────────
 * This is the storefront's session-establishing call, so it is where `?src=`
 * finally reaches the server: the BFF records it on the funnel session and the
 * credit step reads it back when it creates the Accounts prospect (which is what
 * makes a Power to Choose shopper show up as `ptc` in the CRM rather than
 * `organic`). `organic` is omitted rather than sent — it is the server's default,
 * and sending it would put a meaningless parameter on every visitor's first call.
 *
 * The value is attacker-controlled by nature (it came from a URL); the BFF parses
 * it into a closed enum and ignores anything else. Nothing here is a control.
 */
export async function ensureSession(
  source: DeepLinkSource = 'organic',
  options: { readonly fresh?: boolean } = {},
): Promise<SessionResponse> {
  const params = new URLSearchParams();
  if (source !== 'organic') params.set('src', source);
  // `fresh=1` retires whatever session the cookie names and mints a new, empty
  // one — every page load starts over (product decision 2026-09-16).
  if (options.fresh === true) params.set('fresh', '1');
  const query = params.toString();
  const path = query === '' ? BFF_ROUTES.session : `${BFF_ROUTES.session}?${query}`;
  return bffGet(path, SessionResponseSchema);
}

/** `POST /bff/address/lookup` — address → serviceability → ESI resolution. */
export async function postAddressLookup(
  request: AddressLookupRequest,
): Promise<AddressLookupResponse> {
  return bffPost(BFF_ROUTES.addressLookup, request, AddressLookupResponseSchema);
}

/** `POST /bff/lead` — out-of-area lead capture (AC2). */
export async function postLead(request: LeadCaptureRequest): Promise<LeadCaptureResponse> {
  return bffPost(BFF_ROUTES.lead, request, LeadCaptureResponseSchema);
}

/**
 * `GET /bff/address/suggest?q=<partial>` — address autocomplete (demo mock).
 * NO LONGER anonymous: the response now carries each premise's `esiid`, so the
 * BFF gates this GET behind a funnel session + captcha + rate-limit. We therefore
 * send the captcha token (`withCaptcha`) and rely on the session cookie riding via
 * `credentials: 'same-origin'` (the caller mints the session first). ⚠️ The
 * autocomplete-GET captcha caveat is documented in bff-client / STUBS.md.
 */
/**
 * The serviceable city+ZIP list behind the ZIP picker.
 *
 * NO captcha, unlike `getAddressSuggestions` — the BFF route is session-gated
 * only, because it returns a published service territory and no esiid. Sending a
 * token here would be cargo-culted ceremony the server does not check.
 */
export async function getServiceableZips(): Promise<ServiceableZipsResponse> {
  return bffGet(BFF_ROUTES.serviceableZips, ServiceableZipsResponseSchema);
}

export async function getAddressSuggestions(q: string): Promise<AddressSuggestResponse> {
  const path = `${BFF_ROUTES.addressSuggest}?q=${encodeURIComponent(q)}`;
  return bffGet(path, AddressSuggestResponseSchema, { withCaptcha: true });
}
